"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";
import { CodeBlock } from "../../_components/code-block";

const scaffoldCode = `// Scaffold.tsx - Root layout component
export function Scaffold({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background">
        <Navigation />
        <main>{children}</main>
        <ToastProvider />
        <ModalProvider />
      </div>
    </ThemeProvider>
  );
}`;

const pageContainerCode = `// PageContainer.tsx - Page-level wrapper
export function PageContainer({
  children,
  maxWidth = "4xl",
  padding = "md"
}: PageContainerProps) {
  return (
    <div className={cn(
      "mx-auto px-4 py-8",
      maxWidth === "4xl" && "max-w-4xl",
      maxWidth === "6xl" && "max-w-6xl",
      maxWidth === "full" && "max-w-full"
    )}>
      {children}
    </div>
  );
}`;

const sectionCode = `// Section.tsx - Content grouping
export function Section({
  title,
  description,
  children
}: SectionProps) {
  return (
    <section className="space-y-6">
      {title && (
        <div className="space-y-2">
          <h2 className="text-h4 font-semibold">{title}</h2>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      {children}
    </section>
  );
}`;

export default function DocsFoundationsLayout() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Layout System
            </Text>
            <Badge variant="secondary">Foundation</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A hierarchical container system that ensures consistent page structure
            and simplifies global styling changes.
          </Text>
        </div>
      </Section>

      {/* Scaffold Pattern */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>The Scaffold Pattern</CardTitle>
            <CardDescription>
              Root-level component that wraps your entire application
            </CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <Text className="text-muted-foreground">
          All containers are components. Styling applied at the root propagates
          down - no duplicate styles across pages. When adding global styles
          (background gradient, theme, loading states), modify Scaffold - never
          individual pages.
        </Text>

        <div className="bg-muted/30 rounded-lg p-6">
          <div className="border-2 border-dashed border-primary/30 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Badge>Scaffold</Badge>
              <Text className="text-xs text-muted-foreground">
                Theme, navigation, global overlays
              </Text>
            </div>
            <div className="border-2 border-dashed border-primary/30 rounded-lg p-4 ml-4 space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">PageContainer</Badge>
                <Text className="text-xs text-muted-foreground">
                  Max-width, padding, scroll
                </Text>
              </div>
              <div className="border-2 border-dashed border-success/30 rounded-lg p-4 ml-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Section</Badge>
                  <Text className="text-xs text-muted-foreground">
                    Vertical rhythm, spacing
                  </Text>
                </div>
                <div className="mt-3 p-3 bg-muted/50 rounded text-center text-sm text-muted-foreground">
                  Content blocks / Components
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Scaffold Responsibilities */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Scaffold</CardTitle>
            <CardDescription>Root-level responsibilities</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Text className="font-medium">Responsibilities</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Theme switching (dark/light)</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Global CSS variables injection</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Background gradients/colors</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Navigation (sidebar/header)</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Modal/toast/drawer portals</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Top-level error boundary</div>
              </li>
            </ul>
          </div>
          <div>
            <CodeBlock code={scaffoldCode} language="tsx" showLineNumbers />
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* PageContainer */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>PageContainer</CardTitle>
            <CardDescription>Page-level wrapper component</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Text className="font-medium">Responsibilities</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Max-width constraint</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Page-level padding</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Page-specific backgrounds</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Scroll behavior</div>
              </li>
            </ul>
          </div>
          <div>
            <CodeBlock
              code={pageContainerCode}
              language="tsx"
              showLineNumbers
            />
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Section */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Section</CardTitle>
            <CardDescription>Content grouping component</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Text className="font-medium">Responsibilities</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Vertical spacing between sections</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Section dividers if needed</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Section-level error boundaries (optional)</div>
              </li>
              <li className="flex gap-2">
                <div className="text-primary">-</div>
                <div>Content grouping with titles</div>
              </li>
            </ul>
          </div>
          <div>
            <CodeBlock code={sectionCode} language="tsx" showLineNumbers />
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Common Layouts */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Common Layout Patterns</CardTitle>
            <CardDescription>
              Frequently used page structures
            </CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Single Column */}
          <div className="space-y-2">
            <Text className="font-medium text-sm">Single Column</Text>
            <div className="border rounded-lg p-3 h-32 flex flex-col">
              <div className="h-3 bg-muted rounded mb-2" />
              <div className="flex-1 bg-muted/50 rounded" />
            </div>
            <Text className="text-xs text-muted-foreground">
              Documentation, articles, forms
            </Text>
          </div>

          {/* Sidebar Layout */}
          <div className="space-y-2">
            <Text className="font-medium text-sm">Sidebar + Content</Text>
            <div className="border rounded-lg p-3 h-32 flex gap-2">
              <div className="w-1/4 bg-muted rounded" />
              <div className="flex-1 bg-muted/50 rounded" />
            </div>
            <Text className="text-xs text-muted-foreground">
              Dashboards, settings, navigation
            </Text>
          </div>

          {/* Two Column */}
          <div className="space-y-2">
            <Text className="font-medium text-sm">Two Column</Text>
            <div className="border rounded-lg p-3 h-32 flex gap-2">
              <div className="flex-1 bg-muted/50 rounded" />
              <div className="w-1/3 bg-muted rounded" />
            </div>
            <Text className="text-xs text-muted-foreground">
              Detail views, split screens
            </Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Best Practices */}
      <Section>
        <SectionCard
        header={<CardTitle>Best Practices</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Text className="font-semibold text-success">Do</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-success">-</div>
                Use Scaffold for global changes
              </li>
              <li className="flex gap-2">
                <div className="text-success">-</div>
                Keep PageContainer simple and focused
              </li>
              <li className="flex gap-2">
                <div className="text-success">-</div>
                Use semantic Section components
              </li>
              <li className="flex gap-2">
                <div className="text-success">-</div>
                Maintain consistent spacing tokens
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <Text className="font-semibold text-destructive">Don&apos;t</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-destructive">-</div>
                Add global styles to individual pages
              </li>
              <li className="flex gap-2">
                <div className="text-destructive">-</div>
                Skip the container hierarchy
              </li>
              <li className="flex gap-2">
                <div className="text-destructive">-</div>
                Use arbitrary padding/margin values
              </li>
              <li className="flex gap-2">
                <div className="text-destructive">-</div>
                Nest PageContainers within each other
              </li>
            </ul>
          </div>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
